import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useEnv } from '../contexts/EnvContext';

export function useDashboard() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['dashboard', env],
    queryFn: () => api.getDashboard(env),
    refetchInterval: 60 * 1000,
  });
}

export function useQueueStatus(days = 7) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['queue-status', days, env],
    queryFn: () => api.getQueueStatus(days, env),
    refetchInterval: 30 * 1000,
  });
}

export function useErrorHistory(hours = 24) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['failed-credentials', hours, env],
    queryFn: () => api.getErrorHistory(hours, env),
    refetchInterval: 60 * 1000,
  });
}

export function useRetryFailed(days: number) {
  const { env } = useEnv();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (organisationId: string) => api.retryFailedByOrganisation(organisationId, days, env),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['queue-status'] });
      queryClient.invalidateQueries({ queryKey: ['failed-credentials'] });
      alert(`Ponowiono przetwarzanie ${data.retried} credentiali`);
    },
    onError: (error: Error) => {
      alert(`Blad retry: ${error.message}`);
    },
  });
}

export function useAutoRetryStatus() {
  return useQuery({
    // The canary is scoped to prod in the backend, so its cache key carries no env.
    queryKey: ['auto-retry-status'],
    queryFn: () => api.getAutoRetryStatus(),
    refetchInterval: 30 * 1000,
  });
}

export function useSaveAutoRetryConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: Partial<import('../types/api').AutoRetryConfig>) =>
      api.saveAutoRetryConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-retry-status'] });
    },
  });
}

export function useRunAutoRetryCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.runAutoRetryCycle(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-retry-status'] });
      queryClient.invalidateQueries({ queryKey: ['queue-status'] });
      queryClient.invalidateQueries({ queryKey: ['failed-credentials'] });
    },
  });
}

export function useStepPerformance(minutes = 1440) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['step-performance', minutes, env],
    queryFn: () => api.getStepPerformance(minutes, env),
  });
}

export function useOrganisationPerformance(minutes = 10080) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['organisation-performance', minutes, env],
    queryFn: () => api.getOrganisationPerformance(minutes, env),
  });
}

export function useWalletDelivery() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['wallet-delivery', env],
    queryFn: () => api.getWalletDelivery(env),
    refetchInterval: 60 * 1000,
  });
}

export function useEmailDelivery(days = 7) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['email-delivery', days, env],
    queryFn: () => api.getEmailDelivery(days, env),
    refetchInterval: 60 * 1000,
  });
}

export function usePdfSigningBatches(hours = 24) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['pdf-signing-batches', hours, env],
    queryFn: () => api.getPdfSigningBatches(hours, env),
    refetchInterval: 30 * 1000,
  });
}

export function useJsonLdSigningBatches(hours = 24) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['jsonld-signing-batches', hours, env],
    queryFn: () => api.getJsonLdSigningBatches(hours, env),
    refetchInterval: 30 * 1000,
  });
}

export function useSealConfigurations() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['seal-configurations', env],
    queryFn: () => api.getSealConfigurations(env),
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useSealWarnings() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['seal-warnings', env],
    queryFn: () => api.getSealWarnings(env),
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useOrganisationList() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['organisation-list', env],
    queryFn: () => api.getOrganisationList(env),
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrganisationCredits() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['organisation-credits', env],
    queryFn: () => api.getOrganisationCredits(env),
    refetchInterval: 60 * 1000,
  });
}

export function useHealth() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['health', env],
    queryFn: () => api.getHealth(env),
    refetchInterval: 30 * 1000,
  });
}

// These are global (not per-env)
export function useCurrentUser() {
  return useQuery({
    queryKey: ['current-user'],
    queryFn: api.getCurrentUser,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: api.getUsers,
  });
}

// Email Templates
export function useEmailTemplates() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['email-templates', env],
    queryFn: () => api.getEmailTemplates(env),
  });
}

export function useEmailTemplate(database: string, templateName: string, language: string) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['email-template', database, templateName, language, env],
    queryFn: () => api.getEmailTemplate(database, templateName, language, env),
    enabled: !!database && !!templateName && !!language,
  });
}

export function usePlaceholderInfo(templateName: string) {
  return useQuery({
    queryKey: ['placeholder-info', templateName],
    queryFn: () => api.getPlaceholderInfo(templateName),
    enabled: !!templateName,
  });
}

// Credential Templates
export function useCredentialTemplates(archived: boolean) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['credential-templates', archived, env],
    queryFn: () => api.getCredentialTemplates(archived, env),
  });
}

// Certificate Templates
export function useCertificateDesigns() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['certificate-designs', env],
    queryFn: () => api.getCertificateDesigns(env),
  });
}

export function useCertificateDesign(slug: string) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['certificate-design', slug, env],
    queryFn: () => api.getCertificateDesign(slug, env),
    enabled: !!slug,
  });
}

export function useGlobalFonts() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['global-fonts', env],
    queryFn: () => api.getGlobalFonts(env),
  });
}

// Alerting (global, not per-env)

export function useAlertRecipients() {
  return useQuery({
    queryKey: ['alert-recipients'],
    queryFn: api.getAlertRecipients,
  });
}

export function useAlertRules() {
  return useQuery({
    queryKey: ['alert-rules'],
    queryFn: api.getAlertRules,
  });
}

export function useSmtpConfig() {
  return useQuery({
    queryKey: ['smtp-config'],
    queryFn: api.getSmtpConfig,
  });
}

export function useAlertHistory(
  page: number = 1,
  status: import('../types/api').AckStatus | 'all' = 'all',
  pageSize: number = 50,
) {
  return useQuery({
    queryKey: ['alert-history', page, status, pageSize],
    queryFn: () => api.getAlertHistory(page, status, pageSize),
  });
}

export function useUpdateAlertHistoryStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'acknowledged' | 'cleared' }) =>
      api.updateAlertHistoryStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-history'] });
    },
  });
}

// Issuance Test (per-env)
export function useIssuanceTestConfig() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['issuance-test-config', env],
    queryFn: () => api.getIssuanceTestConfig(env),
    staleTime: 60 * 1000,
  });
}

export function useSaveIssuanceTestConfig() {
  const { env } = useEnv();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { apiKey?: string; recipientDomain?: string }) =>
      api.saveIssuanceTestConfig(data, env),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issuance-test-config', env] });
      queryClient.invalidateQueries({ queryKey: ['issuance-test-templates', env] });
    },
  });
}

export function useIssuanceTestTemplates(enabled: boolean) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['issuance-test-templates', env],
    queryFn: () => api.getIssuanceTestTemplates(env),
    enabled,
    retry: false,
  });
}

export function useIssuanceTestRuns() {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['issuance-test-runs', env],
    queryFn: () => api.getIssuanceTestRuns(env),
  });
}

export function useStartIssuanceTestRun() {
  const { env } = useEnv();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { templateId: string; count: number; grade?: string }) =>
      api.startIssuanceTestRun(data, env),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issuance-test-runs', env] });
    },
  });
}

// Polls while a run is still processing; stops once completed.
export function useIssuanceTestRunResult(runId: string | null) {
  const { env } = useEnv();
  return useQuery({
    queryKey: ['issuance-test-run-result', runId, env],
    queryFn: () => api.getIssuanceTestRunResult(runId as string, env),
    enabled: !!runId,
    refetchInterval: (query) =>
      query.state.data && query.state.data.status === 'completed' ? false : 4000,
  });
}
